import type { Patient } from "fhir/r5";
import { GetInstitutionResources } from "@/services/consent";
import { GetEcommercePatientResources, GetPatientsBatch } from "@/services/fhir";
import {
  hydratePurchaseReleasesFromDispenses,
  USE_ECOMMERCE_RESOURCES_BATCH_DISPENSE,
} from "src-next/modules/patient-supporting-documents";
import { isPatientResource } from "../helpers/isPatientResource";
import { ecommercePatientReference } from "../helpers/patientEcommerceOpState";
import {
  rememberEmptyPatientDocSummary,
  rememberPatientDocsFromRaw,
} from "../helpers/patientDocStatusCache";

export interface PreloadPatientResourcesParams {
  patients: Patient[];
  organizationId: string;
  organizationName: string;
}

export interface PreloadPatientResourcesResult {
  ok: boolean;
  /** true se o batch de MedicationDispense hidratou o cache de liberações. */
  dispensesPreloaded: boolean;
}

/**
 * IO da lista ecommerce: consentimento → MPI batch → pré-carga STATISTICS.
 */
export const ecommercePatientsService = {
  async loadConsentPatients(cnpj: string): Promise<Patient[]> {
    const response = await GetInstitutionResources({ cnpj });
    const patientIds = [
      ...new Set(
        (response?.data || [])
          .map((resource) => resource.id?.trim())
          .filter((id): id is string => Boolean(id)),
      ),
    ];

    const batchResponse = await GetPatientsBatch({ identifiers: patientIds });
    return (batchResponse.data || []).filter(isPatientResource);
  },

  /** Deep link / F5 da rota Gerenciar — reusa o mesmo contrato de consentimento. */
  async resolveConsentPatientByKey(
    cnpj: string,
    patientKey: string,
  ): Promise<Patient | null> {
    const key = patientKey.trim();
    if (!cnpj || !key) return null;
    const patients = await this.loadConsentPatients(cnpj);
    return (
      patients.find(
        (patient) => ecommercePatientReference(patient) === key,
      ) ?? null
    );
  },

  async preloadPatientResources(
    params: PreloadPatientResourcesParams,
  ): Promise<PreloadPatientResourcesResult> {
    const { patients, organizationId, organizationName } = params;

    const resources = await GetEcommercePatientResources({
      patients,
      organizationId,
    });

    if (!resources.ok) {
      return { ok: false, dispensesPreloaded: false };
    }

    for (const patient of patients) {
      const key = patient.id ? `Patient/${patient.id}` : "";
      if (!key) continue;
      const documents = resources.documentsByPatient.get(key) ?? [];
      if (documents.length) {
        rememberPatientDocsFromRaw(key, documents, organizationId);
      } else {
        rememberEmptyPatientDocSummary(key);
      }
    }

    const dispensesPreloaded =
      USE_ECOMMERCE_RESOURCES_BATCH_DISPENSE && resources.dispensesOk;

    if (dispensesPreloaded) {
      hydratePurchaseReleasesFromDispenses(
        resources.dispensesByPatient,
        organizationId,
        organizationName,
      );
    }

    return { ok: true, dispensesPreloaded };
  },
};
